/*
 * Quelle Outlet — size pills
 * Renders the size <select> (data-size-select) as tappable pills. The select stays in the
 * DOM as the single source of truth, so Horizon's variant picker and the size guide keep
 * working unchanged. Adding to cart without an explicit size choice is stopped with a hint.
 */
(() => {
  if (window.__qoSizePills) return;
  window.__qoSizePills = true;

  document.documentElement.classList.add('qo-pills-ready');

  const state = (window.__zefforExplicitSizeSelections = window.__zefforExplicitSizeSelections || {});
  const keyFor = (pid) => `zeffor-size-selected-${pid}`;
  const HINT_TEXT = 'Bitte wähle zuerst deine Größe.';

  const pickerOf = (el) => el?.closest('variant-picker');
  const pidOf = (el) => pickerOf(el)?.dataset.productId || null;

  function sync(select) {
    const picker = pickerOf(select);
    const pid = pidOf(select);
    if (!picker || !pid) return;
    const pills = picker.querySelector('[data-qo-size-pills]');
    if (!pills) return;

    const count = Number(pills.dataset.sizeCount || 0);
    if (count === 1) state[keyFor(pid)] = true; // Einheitsgröße

    const explicit = state[keyFor(pid)] === true;
    const current = select.value;

    pills.querySelectorAll('[data-qo-size-pill]').forEach((pill) => {
      pill.setAttribute('aria-pressed', explicit && pill.dataset.value === current ? 'true' : 'false');
    });

    if (explicit) {
      pills.classList.remove('is-attention');
      const hint = picker.querySelector('[data-qo-size-hint]');
      if (hint) hint.hidden = true;
    }
  }

  function build(select) {
    const picker = pickerOf(select);
    if (!picker || !pidOf(select)) return;
    const wrapper = select.closest('.variant-option__select-wrapper');
    if (!wrapper) return;
    const option = wrapper.parentElement;
    if (!option || option.querySelector('[data-qo-size-pills]')) {
      sync(select);
      return;
    }

    const pills = document.createElement('div');
    pills.className = 'qo-size-pills';
    pills.dataset.qoSizePills = '';
    pills.setAttribute('role', 'group');
    pills.setAttribute('aria-label', 'Größe wählen');
    pills.dataset.sizeCount = String(select.options.length);

    for (const opt of select.options) {
      const available = opt.dataset.optionAvailable !== 'false';
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'qo-size-pill' + (available ? '' : ' is-unavailable');
      pill.dataset.qoSizePill = '';
      pill.dataset.value = opt.value;
      pill.setAttribute('aria-pressed', 'false');
      pill.textContent = opt.value;
      if (!available) pill.setAttribute('aria-label', `${opt.value} – ausverkauft`);
      pills.appendChild(pill);
    }

    const hint = document.createElement('p');
    hint.className = 'qo-size-hint';
    hint.dataset.qoSizeHint = '';
    hint.hidden = true;
    hint.textContent = HINT_TEXT;

    wrapper.insertAdjacentElement('afterend', pills);
    pills.insertAdjacentElement('afterend', hint);
    sync(select);
  }

  function buildAll() {
    document.querySelectorAll('variant-picker [data-size-select]').forEach(build);
  }

  // Pill click → set select → Horizon handles the variant change
  document.addEventListener('click', (event) => {
    const pill = event.target.closest?.('[data-qo-size-pill]');
    if (!pill) return;
    event.preventDefault();

    const picker = pickerOf(pill);
    const select = picker?.querySelector('[data-size-select]');
    const pid = picker?.dataset.productId;
    if (!select || !pid) return;

    state[keyFor(pid)] = true;

    if (select.value !== pill.dataset.value) {
      select.value = pill.dataset.value;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    sync(select);
  });

  // Any select change (pills, size guide, Horizon) → keep pills in sync
  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-size-select]');
    if (select) sync(select);
  });

  // Add to cart without a chosen size → stop and nudge.
  // Horizon binds its own "on:submit" / "on:click" handlers on document (capture), so the
  // gate has to sit on window (capture) to run before them.
  function gate(event, form) {
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;

    const pid = form.closest('product-form-component')?.dataset.productId;
    if (!pid) return;

    const scope = form.closest('dialog, .quick-add-modal') || document;
    const picker =
      scope.querySelector(`variant-picker[data-product-id="${pid}"]`) ||
      document.querySelector(`variant-picker[data-product-id="${pid}"]`);
    const pills = picker?.querySelector('[data-qo-size-pills]');
    if (!picker || !pills) return;
    if (state[keyFor(pid)] === true) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const hint = picker.querySelector('[data-qo-size-hint]');
    if (hint) hint.hidden = false;
    pills.classList.remove('is-attention');
    void pills.offsetWidth;
    pills.classList.add('is-attention');

    const rect = pills.getBoundingClientRect();
    if (rect.top < 80 || rect.bottom > window.innerHeight - 80) {
      pills.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  window.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('button[type="submit"], input[type="submit"]');
      if (!button || !button.form) return;
      gate(event, button.form);
    },
    true
  );

  window.addEventListener('submit', (event) => gate(event, event.target), true);

  // Re-build after Horizon morphs the picker (variant change, quick add, section render)
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      buildAll();
    }, 0);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildAll);
  } else {
    buildAll();
  }
})();
